import Link, { LinkProps } from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from '../../utils/routeHelper';
import {
	useGetDay21DetailQuery,
	useGetDay21SingleDetailQuery,
} from '@/api/exercise/day21ApiSlice';
import {
	useGetMeditationDetailQuery,
	useGetSingleDetailQuery,
} from '@/api/exercise/meditationApiSlice';
import {
	useGetBodyDetailQuery,
	useGetBodySingleDetailQuery,
} from '@/api/exercise/bodyApiSlice';
import {
	useGetCrossoverDetailQuery,
	useGetCrossoverSingleDetailQuery,
} from '@/api/exercise/crossoverApiSlice';
import {
	useGetMajorDetailQuery,
	useGetMarjorSingleDetailQuery,
} from '@/api/exercise/majorApiSlice';
import {
	useChapterDetailQuery,
	useInformationDetailQuery,
} from '@/api/information/informationApiSlice';
import { useGetFaqDetailQuery } from '@/api/information/faqApiSlice';
import { useSubFaqDetailQuery } from '@/api/information/subFaqApiSlice';
import { useGetEmoDetailQuery } from '@/api/information/emoApiSlice';
import { useGetAboutUsDetailQuery } from '@/api/information/aboutUsApiSlice';
import { useGetMomentDetailQuery } from '@/api/app-setting/momentApiSlice';
import { useGetDialogDetailQuery } from '@/api/app-setting/dialogApiSlice';
import { useGetRegularDetailQuery } from '@/api/task/regularApiSlice';
import {
	useGetAchievementDetailQuery,
	useGetAchievementSingleDetailQuery,
} from '@/api/task/achievementApiSlice';
import { useGetAchievementTreasureDetailQuery } from '@/api/task/treasureApiSlice';
import {
	useGetActivityDetailQuery,
	useGetActivitySingleDetailQuery,
} from '@/api/task/activityApiSlice';
import {
	useGetGalleryDetailQuery,
	useGetGalleryTreasureDetailQuery,
} from '@/api/treasury/galleryApiSlice';
import {
	useGetPuzzleDetailQuery,
	useGetPuzzleTreasureDetailQuery,
} from '@/api/treasury/puzzleApiSlice';
import {
	useGetStampDetailQuery,
	useGetStampTreasureDetailQuery,
} from '@/api/treasury/stampApiSlice';
import {
	useGetWishDetailQuery,
	useGetWishTreasureDetailQuery,
} from '@/api/treasury/wishApiSlice';
import {
	useGetChartletDetailQuery,
	useGetTreasureDetailQuery,
} from '@/api/treasury/chartletApiSlice';
import { useGetBoxDetailQuery } from '@/api/exchange/boxApiSlice';
import { useGetRuleDeleteQuery } from '@/api/task/ruleApiSlice';
import { useGetHealthDetailQuery } from '@/api/app-setting/healthApiSlice';

interface LinkRouterProps extends LinkProps {
	to: string;
	replace?: boolean;
}

function LinkRouter(props: LinkRouterProps) {
	return <Link {...props} component={RouterLink} />;
}

export default function RouterBreadcrumbs() {
	const { t } = useTranslation();
	const location = useLocation();
	const pathnames = location.pathname.split('/').filter((x) => x);
	const {
		day21Id,
		day21SingleId,
		meditationId,
		meditationSingleId,
		bodyId,
		bodySingleId,
		crossoverId,
		crossoverSingleId,
		majorId,
		majorSingleId,
		informationId,
		chapterId,
		faqId,
		questionId,
		emoId,
		aboutId,
		momentId,
		dialogId,
		regularDayId,
		regularWeekId,
		regularMonthId,
		achievementId,
		treasureId,
		activityId,
		activitySingleId,
		galleryId,
		gallerySingleId,
		puzzleId,
		puzzleSingleId,
		stampId,
		stampSingleId,
		wishId,
		wishSingleId,
		chartletId,
		chartletSingleId,
		boxId,
		ruleId,
		healthId,
	} = useParams();
	let lastPath = '';

	const { data: day21Detail } = useGetDay21DetailQuery(day21Id || '', {
		refetchOnMountOrArgChange: true,
		skip: !day21Id,
	});
	const { data: day21SingleDetail } = useGetDay21SingleDetailQuery(
		day21SingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !day21SingleId,
		}
	);
	const { data: meditationDetails } = useGetMeditationDetailQuery(
		meditationId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !meditationId,
		}
	);
	const { data: meditationSingleDetails } = useGetSingleDetailQuery(
		meditationSingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !meditationSingleId,
		}
	);
	const { data: bodyDetails } = useGetBodyDetailQuery(bodyId || '', {
		refetchOnMountOrArgChange: true,
		skip: !bodyId,
	});
	const { data: bodySingleDetails } = useGetBodySingleDetailQuery(
		bodySingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !bodySingleId,
		}
	);
	const { data: crossoverDetails } = useGetCrossoverDetailQuery(
		crossoverId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !crossoverId,
		}
	);
	const { data: crossoverSingleDetails } = useGetCrossoverSingleDetailQuery(
		crossoverSingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !crossoverSingleId,
		}
	);
	const { data: majorDetails } = useGetMajorDetailQuery(majorId || '', {
		refetchOnMountOrArgChange: true,
		skip: !majorId,
	});
	const { data: majorSingleDetails } = useGetMarjorSingleDetailQuery(
		majorSingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !majorSingleId,
		}
	);
	const { data: informationDetails } = useInformationDetailQuery(
		informationId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !informationId,
		}
	);
	const { data: chapterDetails } = useChapterDetailQuery(chapterId || '', {
		refetchOnMountOrArgChange: true,
		skip: !chapterId,
	});
	const { data: faqDetails } = useGetFaqDetailQuery(faqId || '', {
		refetchOnMountOrArgChange: true,
		skip: !faqId,
	});
	const { data: questionDetails } = useSubFaqDetailQuery(questionId || '', {
		refetchOnMountOrArgChange: true,
		skip: !questionId,
	});
	const { data: emoDetails } = useGetEmoDetailQuery(emoId || '', {
		refetchOnMountOrArgChange: true,
		skip: !emoId,
	});
	const { data: aboutDetails } = useGetAboutUsDetailQuery(aboutId || '', {
		refetchOnMountOrArgChange: true,
		skip: !aboutId,
	});
	const { data: momentDetails } = useGetMomentDetailQuery(momentId || '', {
		refetchOnMountOrArgChange: true,
		skip: !momentId,
	});
	const { data: dialogDetails } = useGetDialogDetailQuery(dialogId || '', {
		refetchOnMountOrArgChange: true,
		skip: !dialogId,
	});
	const { data: regularDayDetails } = useGetRegularDetailQuery(
		regularDayId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !regularDayId,
		}
	);
	const { data: regularMonthDetails } = useGetRegularDetailQuery(
		regularMonthId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !regularMonthId,
		}
	);
	const { data: regularWeekDetails } = useGetRegularDetailQuery(
		regularWeekId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !regularWeekId,
		}
	);
	const { data: achievementDetails } = useGetAchievementDetailQuery(
		achievementId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !achievementId,
		}
	);
	const { data: treasureDetails } = useGetAchievementTreasureDetailQuery(
		treasureId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !treasureId,
		}
	);
	const { data: activityDetails } = useGetActivityDetailQuery(
		activityId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !activityId,
		}
	);
	const { data: activitySingleDetails } = useGetActivitySingleDetailQuery(
		activitySingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !activitySingleId,
		}
	);
	const { data: galleryDetails } = useGetGalleryDetailQuery(galleryId || '', {
		refetchOnMountOrArgChange: true,
		skip: !galleryId,
	});
	const { data: gallerySingleDetails } = useGetGalleryTreasureDetailQuery(
		gallerySingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !gallerySingleId,
		}
	);
	const { data: puzzleDetails } = useGetPuzzleDetailQuery(puzzleId || '', {
		refetchOnMountOrArgChange: true,
		skip: !puzzleId,
	});
	const { data: puzzleSingleDetails } = useGetPuzzleTreasureDetailQuery(
		puzzleSingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !puzzleSingleId,
		}
	);
	const { data: stampDetails } = useGetStampDetailQuery(stampId || '', {
		refetchOnMountOrArgChange: true,
		skip: !stampId,
	});
	const { data: stampSingleDetails } = useGetStampTreasureDetailQuery(
		stampSingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !stampSingleId,
		}
	);
	const { data: wishDetails } = useGetWishDetailQuery(wishId || '', {
		refetchOnMountOrArgChange: true,
		skip: !wishId,
	});
	const { data: wishSingleDetails } = useGetWishTreasureDetailQuery(
		wishSingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !wishSingleId,
		}
	);
	const { data: chartletDetails } = useGetChartletDetailQuery(
		chartletId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !chartletId,
		}
	);
	const { data: chartletSingleDetails } = useGetTreasureDetailQuery(
		chartletSingleId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !chartletSingleId,
		}
	);
	const { data: boxDetails } = useGetBoxDetailQuery(boxId || '', {
		refetchOnMountOrArgChange: true,
		skip: !boxId,
	});
	const { data: ruleDetail } = useGetRuleDeleteQuery(ruleId || '', {
		refetchOnMountOrArgChange: true,
		skip: !ruleId,
	});
	const { data: healthDetail } = useGetHealthDetailQuery(healthId || '', {
		refetchOnMountOrArgChange: true,
		skip: !healthId,
	});
	// You need to provide the routes in descendant order.
	// This means that if you have nested routes like:
	// users, users/new, users/edit.
	// Then the order should be ['users/add', 'users/edit', 'users'].
	const breadcrumbNameMap: { [key: string]: string } = {
		'/staff-manage/staff-list': t('drawerListItems.staffList'),
		'/staff-manage/staff-list/add': t('staff.addStaffAccount'),
		'/staff-manage/staff-list/info/:userId': t('staff.staffInfo'),
		'/user-manage/user-list': t('drawerListItems.userList'),
		'/user-manage/user-list/add': t('user.createUser'),
		'/user-manage/user-list/info/:userId': t('user.userInfo'),
		'/user-manage/user-list/info/:userId/series-21': t('user.record21List'),
		'/user-manage/user-list/info/:userId/series-21/:seriesId':
			t('day21.exerciseList'),
		'/user-manage/user-list/info/:userId/major': t('user.majorProgress'),
		'/user-manage/user-list/info/:userId/major/:seriesId':
			t('day21.exerciseList'),
		'/user-manage/delete-user-record': t('drawerListItems.deleteUserRecord'),
		'/app-setting/clock-list': t('drawerListItems.clockList'),
		'/app-setting/moment-list': t('drawerListItems.momentList'),
		'/app-setting/moment-list/info/:momentId':
			momentDetails?.data.titleZh || t('drawerListItems.momentDetail'),
		'/app-setting/health-list': t('drawerListItems.healthList'),
		'/app-setting/health-list/info/:id':
			healthDetail?.data.typeLabel || t('drawerListItems.healthDetail'),

		'/app-setting/dialog-list': t('drawerListItems.dialogList'),
		'/app-setting/dialog-list/add': t('drawerListItems.createDialog'),
		'/app-setting/dialog-list/info/:dialogId':
			dialogDetails?.data.titleZh || t('drawerListItems.dialogDetail'),

		'/information-manage/emo-support-list': t('drawerListItems.emoSupportList'),
		'/information-manage/emo-support-list/add': t('drawerListItems.addItem'),
		'/information-manage/emo-support-list/info':
			emoDetails?.data.titleZh || t('drawerListItems.itemDetail'),
		'/information-manage/faq-list': t('drawerListItems.faqList'),
		'/information-manage/faq-list/sub-faq-list/:faqId':
			faqDetails?.data.titleZh || t('drawerListItems.faqListPage'),
		'/information-manage/faq-list/sub-faq-list/:faqId/add': t(
			'drawerListItems.addQuestion'
		),
		'/information-manage/faq-list/sub-faq-list/:faqId/info':
			questionDetails?.data.questionZh || t('drawerListItems.questionDetail'),
		'/information-manage/about-us-list': t('drawerListItems.aboutUsList'),
		'/information-manage/about-us-list/add': t('drawerListItems.addPage'),
		'/information-manage/about-us-list/info':
			aboutDetails?.data.titleZh || t('drawerListItems.pageDetail'),
		'/information-manage/information-list': t(
			'drawerListItems.informationList'
		),
		'/information-manage/information-list/add': t(
			'drawerListItems.addInformation'
		),
		'/information-manage/information-list/info/:informationId':
			informationDetails?.data.titleZh ||
			t('drawerListItems.informationDetail'),
		'/information-manage/information-list/info/:informationId/add': t(
			'drawerListItems.addChapter'
		),
		'/information-manage/information-list/info/:informationId/chapter-info':
			chapterDetails?.data.titleZh || t('drawerListItems.chapterDetail'),
		'/app-setting/notification-list': t('drawerListItems.notificationList'),
		'/exercise-manage/meditation-list': t('drawerListItems.meditation'),
		'/exercise-manage/meditation-list/add': t('drawerListItems.addMeditation'),
		'/exercise-manage/meditation-list/info/:meditationId':
			meditationDetails?.data.titleZh || t('drawerListItems.meditationDetail'),
		'/exercise-manage/meditation-list/info/:meditationId/add': t(
			'drawerListItems.addSingle'
		),
		'/exercise-manage/meditation-list/info/:meditationId/detail':
			meditationSingleDetails?.data.titleZh ||
			t('drawerListItems.singleDetail'),

		'/exercise-manage/body-list': t('drawerListItems.body'),
		'/exercise-manage/body-list/add': t('drawerListItems.addBody'),
		'/exercise-manage/body-list/info/:bodyId':
			bodyDetails?.data.titleZh || t('drawerListItems.bodyDetail'),
		'/exercise-manage/body-list/info/:bodyId/add': t(
			'drawerListItems.addSingle'
		),
		'/exercise-manage/body-list/info/:bodyId/detail':
			bodySingleDetails?.data.titleZh || t('drawerListItems.singleDetail'),

		'/exercise-manage/crossover-list': t('drawerListItems.crossover'),
		'/exercise-manage/crossover-list/add': t('drawerListItems.addCrossover'),
		'/exercise-manage/crossover-list/info/:crossoverId':
			crossoverDetails?.data.titleZh || t('drawerListItems.crossoverDetail'),
		'/exercise-manage/crossover-list/info/:crossoverId/add': t(
			'drawerListItems.addSingle'
		),
		'/exercise-manage/crossover-list/info/:crossoverId/detail':
			crossoverSingleDetails?.data.titleZh || t('drawerListItems.singleDetail'),

		'/exercise-manage/day21-list': t('drawerListItems.day21'),
		'/exercise-manage/day21-list/add': t('drawerListItems.addDay21'),
		'/exercise-manage/day21-list/info/:day21Id':
			day21Detail?.data.titleZh || t('drawerListItems.day21Detail'),
		'/exercise-manage/day21-list/info/:day21Id/create-questionnaire': t(
			'drawerListItems.createQuestionnaire'
		),
		'/exercise-manage/day21-list/info/:day21Id/detail/:day21SingleId':
			day21SingleDetail?.data.titleZh || t('drawerListItems.day21SingleDetail'),
		'/exercise-manage/day21-list/info/:day21Id/detail/:day21SingleId/additional':
			t('drawerListItems.addDay21Additional'),

		'/exercise-manage/ding-day21': t('drawerListItems.exerciseSetting'),
		'/exercise-manage/top-series': t('drawerListItems.exerciseSetting'),
		'/exercise-manage/manage-tag': t('drawerListItems.exerciseSetting'),

		'/exercise-manage/major-list': t('drawerListItems.marjorExercise'),
		'/exercise-manage/major-list/add': t('drawerListItems.addMarjorExercise'),
		'/exercise-manage/major-list/info/:majorId':
			majorDetails?.data.titleZh || t('drawerListItems.marjorExerciseDetail'),
		'/exercise-manage/major-list/info/:majorId/detail/:majorSingleId':
			majorSingleDetails?.data.titleZh ||
			t('drawerListItems.marjorExerciseDetailInfo'),
		'/exercise-manage/major-list/info/:majorId/add': t(
			'drawerListItems.addMarjorExerciseDetailDetail'
		),

		'/task-manage/activity-list': t('drawerListItems.activityList'),
		'/task-manage/activity-list/add': t('drawerListItems.addActivity'),
		'/task-manage/activity-list/info/:activityId':
			activityDetails?.data.titleZh || t('drawerListItems.activityDetail'),
		'/task-manage/activity-list/info/:activityId/add': t(
			'drawerListItems.addActivityTask'
		),
		'/task-manage/activity-list/info/:activityId/detail/:activitySingleId':
			activitySingleDetails?.data.titleZh ||
			t('drawerListItems.activityTaskDetail'),

		'/task-manage/achievement-list': t('drawerListItems.achievementList'),
		'/task-manage/achievement-list/add': t('drawerListItems.addAchievement'),
		'/task-manage/achievement-list/info/:achievementId':
			achievementDetails?.data.titleZh ||
			t('drawerListItems.achievementDetail'),
		'/task-manage/achievement-list/info/:achievementId/add': t(
			'drawerListItems.addAchievementTask'
		),
		'/task-manage/achievement-list/info/:achievementId/detail/:achievementSingleId':
			t('drawerListItems.achievementTaskSingleDetail'),

		'/task-manage/achievement-list/treasure/info/:treasureId':
			treasureDetails?.data.titleZh || t('drawerListItems.treasurDetail'),
		'/task-manage/achievement-list/treasure/info/:treasureId/add': t(
			'drawerListItems.addAchievementTask'
		),
		'/task-manage/achievement-list/treasure/info/:treasureId/detail/:treasureSingleId':
			t('drawerListItems.treasurSingleDetail'),

		'/task-manage/regular-day-list': t('drawerListItems.regularTask'),
		'/task-manage/regular-day-list/add': t('drawerListItems.addRegularDayTask'),
		'/task-manage/regular-day-list/info/:regularDayId':
			regularDayDetails?.data.titleZh ||
			t(t('drawerListItems.regularDayTaskDetail')),

		'/task-manage/regular-day-list/week/add': t(
			'drawerListItems.addRegularWeekTask'
		),
		'/task-manage/regular-day-list/week/info/:regularWeekId':
			regularWeekDetails?.data.titleZh ||
			t('drawerListItems.regularWeekTaskDetail'),

		'/task-manage/regular-day-list/month/add': t(
			'drawerListItems.addRegularMonthTask'
		),
		'/task-manage/regular-day-list/month/info/:regularMonthId':
			regularMonthDetails?.data.titleZh ||
			t('drawerListItems.regularMonthTaskDetail'),

		'/treasury-manage/chartlet-list': t('drawerListItems.chartletList'),
		'/treasury-manage/chartlet-list/add': t('drawerListItems.addChartletList'),
		'/treasury-manage/chartlet-list/info/:chartletId':
			chartletDetails?.data.titleZh || t('drawerListItems.chartletDetail'),
		'/treasury-manage/chartlet-list/info/:chartletId/add': t(
			'drawerListItems.addTreasure'
		),
		'/treasury-manage/chartlet-list/info/:chartletId/detail/:chartletSingleId':
			chartletSingleDetails?.data.titleZh ||
			t('drawerListItems.treasureDetail'),

		'/treasury-manage/wish-list': t('drawerListItems.wishList'),
		'/treasury-manage/wish-list/add': t('drawerListItems.addWishList'),
		'/treasury-manage/wish-list/info/:wishtId':
			wishDetails?.data.titleZh || t('drawerListItems.wishDetail'),
		'/treasury-manage/wish-list/info/:wishtId/add': t(
			'drawerListItems.addTreasure'
		),
		'/treasury-manage/wish-list/info/:wishtId/detail/:wishSingleId':
			wishSingleDetails?.data.titleZh || t('drawerListItems.treasureDetail'),

		'/treasury-manage/stamp-list': t('drawerListItems.stampList'),
		'/treasury-manage/stamp-list/add': t('drawerListItems.addStampList'),
		'/treasury-manage/stamp-list/info/:stampId':
			stampDetails?.data.titleZh || t('drawerListItems.stampDetail'),
		'/treasury-manage/stamp-list/info/:stampId/add': t(
			'drawerListItems.addTreasure'
		),
		'/treasury-manage/stamp-list/info/:stampId/detail/:stampSingleId':
			stampSingleDetails?.data.titleZh || t('drawerListItems.treasureDetail'),

		'/treasury-manage/puzzle-list': t('drawerListItems.puzzleList'),
		'/treasury-manage/puzzle-list/add': t('drawerListItems.addPuzzleList'),
		'/treasury-manage/puzzle-list/info/:puzzleId':
			puzzleDetails?.data.titleZh || t('drawerListItems.puzzleDetail'),
		'/treasury-manage/puzzle-list/info/:puzzleId/add': t(
			'drawerListItems.addTreasure'
		),
		'/treasury-manage/puzzle-list/info/:puzzleId/detail/:puzzleSingleId':
			puzzleSingleDetails?.data.titleZh || t('drawerListItems.treasureDetail'),

		'/treasury-manage/gallery-list': t('drawerListItems.galleryList'),
		'/treasury-manage/gallery-list/add': t('drawerListItems.addGalleryList'),
		'/treasury-manage/gallery-list/info/:galleryId':
			galleryDetails?.data.titleZh || t('drawerListItems.galleryDetail'),
		'/treasury-manage/gallery-list/info/:galleryId/add': t(
			'drawerListItems.addTreasure'
		),
		'/treasury-manage/gallery-list/info/:galleryId/detail/:gallerySingleId':
			gallerySingleDetails?.data.titleZh || t('drawerListItems.treasureDetail'),
		'/treasury-manage/explain': t('drawerListItems.explainText'),

		'/task-manage/rule-list': t('drawerListItems.ruleList'),
		'/task-manage/rule-list/info/:ruleId':
			ruleDetail?.data.titleZh || t('drawerListItems.ruleDetail'),

		'/exchange-manage/week-treasure': t('drawerListItems.weekTreasure'),

		'/exchange-manage/mystery-box': t('drawerListItems.mysteryBox'),
		'/exchange-manage/mystery-box/add': t('drawerListItems.createMysteryBox'),
		'/exchange-manage/mystery-box/info/:boxId':
			boxDetails?.data.titleZh || t('drawerListItems.mysteryBoxDetail'),
		'/exchange-manage/mystery-box/info/:boxId/add': t(
			'drawerListItems.createMysteryBoxSingle'
		),
		'/exchange-manage/mystery-box/info/:boxId/detail/:boxSingleId': t(
			'drawerListItems.mysteryBoxSingleDetail'
		),
	};

	for (let index = pathnames.length - 1; index >= 0; index--) {
		const to = `/${pathnames.slice(0, index + 1).join('/')}`;
		const pathMatch = useRouteMatch(to, Object.keys(breadcrumbNameMap));
		if (pathMatch) {
			lastPath = pathMatch?.pattern?.path;
			break;
		}
	}

	return (
		<Breadcrumbs aria-label='breadcrumb' sx={{ marginBottom: 3 }}>
			<LinkRouter underline='hover' color='inherit' to='/'>
				{t('common.homePage')}
			</LinkRouter>

			{pathnames.map((_, index) => {
				const to = `/${pathnames.slice(0, index + 1).join('/')}`;
				const pathMatch = useRouteMatch(to, Object.keys(breadcrumbNameMap));
				const last = pathMatch?.pattern?.path == lastPath;
				return (
					pathMatch &&
					(last ? (
						<Typography color='text.primary' key={to}>
							{breadcrumbNameMap[pathMatch?.pattern?.path]}
						</Typography>
					) : (
						<LinkRouter underline='hover' color='inherit' to={to} key={to}>
							{breadcrumbNameMap[pathMatch?.pattern?.path]}
						</LinkRouter>
					))
				);
			})}
		</Breadcrumbs>
	);
}
